import {openResourceSection} from '../../core/resourceEvents';
import {highlightExistingAction} from '../../core/actionHighlighter';
import {
	BULK_OPERATIONS_CONFIG_AUDIT_SECTION_EVENT,
	BULK_OPERATIONS_FILTER_EVENT,
	BULK_OPERATIONS_SECTION_EVENT,
	BULK_OPERATIONS_TASKS_SECTION_EVENT,
} from './events';

const OPTIONS_SECTIONS = new Set(['maintenance.bulk_operations']);
const TASKS_SECTIONS = new Set(['maintenance.bulk_operation_tasks']);
const CONFIG_AUDIT_SECTIONS = new Set(['maintenance.bulk_config_audit']);

/**
 * Open the Bulk Operations tab, optionally clicking the existing "Download
 * sample" control once it is there.
 *
 * The sample file is a static asset zipped client-side - there is nothing
 * to resolve or navigate to beyond the tab itself, so this reuses the same
 * open-then-highlight shape alarms/backup already use for a toolbar action
 * (`action_target`), rather than a bespoke download flow.
 *
 * @param {object} command experience command
 * @return {Promise<void>} completion acknowledgement
 */
export async function openBulkOperationsSection(command = {}) {
	const seen = await openResourceSection(
		BULK_OPERATIONS_SECTION_EVENT,
		'maintenance.bulk_operations',
		OPTIONS_SECTIONS,
	);
	if (command.params?.action_target === 'download_sample' &&
		!highlightExistingAction(['Download sample'], [], {
			activate: command.params?.action_activation === 'auto',
			commandId: command.command_id,
		})) {
		throw new Error('bulk_operations_download_sample_unavailable');
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Open the Bulk Operations tab with the Bulk Operation Tasks accordion
 * expanded - the scheduled and completed bulk jobs, not one of the
 * add/delete/action screens above it.
 * @return {Promise<void>} completion acknowledgement
 */
export function openBulkOperationTasksSection() {
	return openResourceSection(
		BULK_OPERATIONS_TASKS_SECTION_EVENT,
		'maintenance.bulk_operation_tasks',
		TASKS_SECTIONS,
	);
}

/**
 * Open the Bulk Operations tab with the Bulk Configuration Audit screen
 * shown - a debug-gated Access Point config-check feature, reached the
 * same way the Tasks table is.
 * @return {Promise<void>} completion acknowledgement
 */
export function openBulkConfigAuditSection() {
	return openResourceSection(
		BULK_OPERATIONS_CONFIG_AUDIT_SECTION_EVENT,
		'maintenance.bulk_config_audit',
		CONFIG_AUDIT_SECTIONS,
	);
}

// The Bulk Operation Tasks table takes one search term, not a filter form -
// see bulk_operations/fields.json. Retried the same way every other
// section/filter handler in this codebase is, in case the page has not
// mounted its listener yet.
const ANSWER_TIMEOUT_MS = 10000;
const RETRY_MS = 120;

/**
 * Apply the Bulk Operation Tasks search filter.
 *
 * One free-text term - status, operation type, device type and schedule
 * time are all matched by the same box, because that is the one query
 * parameter the API behind this table accepts.
 *
 * @param {object} command apply_filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applyBulkOperationsFilter(command) {
	const params = command?.params || {};
	const detail = {search: params.search ?? null};
	return new Promise((resolve, reject) => {
		let settled = false;
		let retry = null;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			if (retry !== null) clearInterval(retry);
			callback();
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('bulk_operations_filter_unavailable'))),
			ANSWER_TIMEOUT_MS,
		);
		const send = () => {
			if (settled) return;
			window.dispatchEvent(new CustomEvent(BULK_OPERATIONS_FILTER_EVENT, {
				detail: {
					...detail,
					complete: () => finish(resolve),
					fail: (reason) => finish(
						() => reject(new Error(reason || 'bulk_operations_filter_failed')),
					),
				},
			}));
		};
		send();
		if (!settled) retry = setInterval(send, RETRY_MS);
	});
}
